import React, {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Icon} from '@thryvlabs/maverick'
import '../../../../../../../styles/kp-11Generic-catalog-card.css'
import {
  getCatalogCollapseStatus,
  updateCatalogCollapseStatus,
} from '../../../../../../../app-store/kp11CartSlice'
import {
  CardPlanHeaderStyle,
  CardPlanBodyStyle,
} from '../../kp-11Link-catalog-cards-style'
import {KP11LinkCatalogCardWrapperStyle} from '../../kp-11Link-catalog-cards-style'
import {fetchPlanIcon} from '../../../../../../../utils/fetch-plan-icon'
import {formatPrice} from '../../../../../../../utils/currency-format'
import {isOneTimeItem} from '../../../../kp-11Link-utility/kp11-utility'

const GenericKP11CardNumberInput = ({
  name,
  price,
  isChecked: isCheckedProp,
  onClick,
  label,
  value,
  onChange,
  placeholder,
  min,
  max,
  isDisabled = false,
  onBlur,
  onSave,
  itemCode,
  groupKey,
  quantityValidationRequired = false,
  existingquantity = 0,
  billingType = 'Billed Monthly',
  selectedCountry = 'US',
}) => {
  const dispatch = useDispatch()
  const cardCollapseStatus = useSelector(getCatalogCollapseStatus)

  const collapseKey = itemCode || groupKey || name || 'generic-number'
  const isExpanded = Boolean(cardCollapseStatus?.[collapseKey])

  useEffect(() => {
    // If parent passes an initial checked/expanded state, sync to redux
    if (typeof isCheckedProp === 'boolean' && isCheckedProp !== isExpanded) {
      dispatch(updateCatalogCollapseStatus(collapseKey))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckedProp])
  useEffect(() => {
    if (
      !isExpanded &&
      (value === undefined || value === null || value === '')
    ) {
      onChange(min !== undefined ? String(min) : '1')
    }
  }, [isExpanded, min, onChange, value])

  // Validation logic
  const numericValue = value === '' ? null : Number(value)
  const existingQtyNum = Number(existingquantity) || 0
  const isValidNumber = value === '' || !isNaN(numericValue)
  const isBelowMin =
    min !== undefined && numericValue !== null && numericValue < min
  const isAboveMax =
    max !== undefined && numericValue !== null && numericValue > max
  const isInvalid = !isValidNumber || isBelowMin || isAboveMax

  const getValidationMessage = () => {
    if (!isValidNumber) return 'Please enter a valid number'
    if (isBelowMin) return `Value must be at least ${min}`
    if (isAboveMax) return `Value must be no more than ${max}`
    return null
  }

  const getRangeHint = () => {
    if (min !== undefined && max !== undefined) {
      return `Range: ${min} - ${max}`
    }
    if (min !== undefined) return `Minimum: ${min}`
    if (max !== undefined) return `Maximum: ${max}`
    return null
  }

  const handleInputChange = e => {
    const inputValue = e.target.value
    if (inputValue.includes('.')) {
      return
    }
    if (inputValue === '' || !isNaN(Number(inputValue))) {
      onChange(inputValue)
    }
  }

  const getClampedValue = inputValue => {
    if (inputValue === '') {
      return min !== undefined ? String(min) : ''
    }

    const numberValue = Number(inputValue)
    if (isNaN(numberValue)) {
      return inputValue
    }

    let clampedValue = numberValue
    if (min !== undefined && clampedValue < min) clampedValue = min
    if (max !== undefined && clampedValue > max) clampedValue = max
    return String(clampedValue)
  }

  const handleBlur = e => {
    const finalValue = getClampedValue(value)
    onBlur?.(e, finalValue)
    onChange(finalValue)
  }

  const handleSaveClick = e => {
    e.stopPropagation()
    const finalValue = getClampedValue(value)
    onChange(finalValue)
    onSave(finalValue)
  }

  const inputPlaceholder =
    placeholder ||
    `Enter value${min !== undefined ? ` (min: ${min})` : ''}${
      max !== undefined ? ` (max: ${max})` : ''
    }`
  return (
    <KP11LinkCatalogCardWrapperStyle>
      <div
        className={`d-flex flex-column w-100 ${
          isDisabled ? 'disable-with-opacity' : ''
        }`}
      >
        <CardPlanHeaderStyle
          onClick={() =>
            !isDisabled && dispatch(updateCatalogCollapseStatus(collapseKey))
          }
        >
          <div className="card-heading justify-content-between w-100">
            <div className="d-flex flex-row align-items-center gap-4">
              <div className="card-icon">
                <img
                  src={fetchPlanIcon(name)?.icon || ''}
                  alt="plan logo"
                  className="options-logo rounded"
                />
              </div>
              <div className="card-title-text">{name || 'Item'}</div>
            </div>
            <div>
              <Icon
                type={'solid'}
                variant={isExpanded ? 'caretDown' : 'caretRight'}
                height={'15'}
                width={'15'}
                color={'#FF6100'}
              />
            </div>
          </div>
        </CardPlanHeaderStyle>

        {isExpanded && (
          <CardPlanBodyStyle>
            <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{paddingTop: '1rem'}}>
                  <div className="plan-item-price">
                    {formatPrice(price ?? 0, 1, '', selectedCountry)}
                  </div>
                  {!isOneTimeItem(itemCode) && (
                    <div className="card-plan-subText">{billingType}</div>
                  )}
                </div>
              </div>

              <div
                className="custom-field-wrapper"
                style={{marginTop: '12px'}}
                onClick={e => e.stopPropagation()}
              >
                <label className="custom-field-label">
                  <span className="custom-field-label-text">{label}</span>
                  <input
                    type="number"
                    step="1"
                    className={`custom-field-input ${
                      isInvalid && value !== '' ? 'error' : ''
                    }`}
                    min={min}
                    max={max}
                    value={value}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder={inputPlaceholder}
                  />
                  {isInvalid && value !== '' && (
                    <span className="custom-field-error">
                      {getValidationMessage()}
                    </span>
                  )}
                  {(min !== undefined || max !== undefined) && !isInvalid && (
                    <span className="custom-field-hint">{getRangeHint()}</span>
                  )}
                  {quantityValidationRequired &&
                    existingQtyNum > 0 &&
                    !isNaN(value) &&
                    Number(value) <= existingQtyNum && (
                      <span className="custom-field-hint">{`Existing quantity is ${existingQtyNum}. For upgrade choose value greater than ${existingQtyNum}`}</span>
                    )}
                </label>
              </div>

              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="add-to-cart-button"
                  onClick={handleSaveClick}
                  disabled={
                    isDisabled ||
                    isInvalid ||
                    value === '' ||
                    (quantityValidationRequired &&
                      Number(value) <= existingQtyNum)
                  }
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </CardPlanBodyStyle>
        )}
      </div>
    </KP11LinkCatalogCardWrapperStyle>
  )
}
export default GenericKP11CardNumberInput
